import { useQuery } from '@tanstack/react-query'
import type { IPublicSignupSurveyResolve } from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'

const publicConfig = { skipUnauthorizedRedirect: true } as const

export type PublicSignupSurveyLookup = {
  slug?: string
  instrumentId?: string
}

function publicSignupSurveyPath(lookup: PublicSignupSurveyLookup): string | null {
  const instrumentId = lookup.instrumentId?.trim() ?? ''
  if (instrumentId) {
    return `/api/v1/public/signup-surveys/id/${encodeURIComponent(instrumentId)}`
  }
  const slug = lookup.slug?.trim() ?? ''
  if (slug) {
    return `/api/v1/public/signup-surveys/${encodeURIComponent(slug)}`
  }
  return null
}

export async function fetchPublicSignupSurvey(
  lookup: PublicSignupSurveyLookup,
): Promise<IPublicSignupSurveyResolve> {
  const path = publicSignupSurveyPath(lookup)
  if (!path) {
    throw new Error('Sign-up Survey not found')
  }
  const { data } = await apiClient.get<ApiResponse<IPublicSignupSurveyResolve>>(
    path,
    publicConfig,
  )
  if (!data.data?.instrumentId) {
    throw new Error('Sign-up Survey not found')
  }
  return data.data
}

export function usePublicSignupSurvey(lookup: PublicSignupSurveyLookup | undefined) {
  const instrumentId = lookup?.instrumentId?.trim() ?? ''
  const slug = lookup?.slug?.trim() ?? ''
  const enabled = instrumentId.length > 0 || slug.length > 0
  return useQuery({
    queryKey: instrumentId
      ? queryKeys.publicSignupSurvey.byInstrumentId(instrumentId)
      : queryKeys.publicSignupSurvey.bySlug(slug),
    queryFn: () => fetchPublicSignupSurvey({ slug, instrumentId }),
    enabled,
    retry: false,
  })
}
